import { supabase } from '@/integrations/supabase/client';
import type { UseResearchLibraryOptions } from './types';

// Enhanced query builder with proper JSONB handling
export class ResearchQueryBuilder {
  private query: any;
  private baseSelect = `
    *,
    research_favorites!left(id)
  `;

  constructor(userId: string, scope: 'all' | 'mine' = 'mine') {
    this.query = supabase
      .from('research_library')
      .select(this.baseSelect);
    
    // Only filter by user_id if scope is 'mine'
    if (scope === 'mine') {
      this.query = this.query.eq('user_id', userId);
    }
  }

  // Apply text-based search filters with keyword-based AND logic
  applyTextSearch(searchQuery?: string) {
    if (searchQuery) {
      console.log(`🔍 Adding keyword-based text search filter: "${searchQuery}"`);
      console.log(`🔍 Search will look in: title, content, original_prompt`);
      
      // Split search query into individual keywords and trim whitespace
      const keywords = searchQuery.trim().split(/\s+/).filter(keyword => keyword.length > 0);
      
      if (keywords.length === 0) {
        return this;
      }
      
      console.log(`🔍 Processing ${keywords.length} keyword(s):`, keywords);
      
      // Apply each keyword as a separate filter (creates AND logic between keywords)
      keywords.forEach((keyword) => {
        // Each keyword must appear in at least one of the three fields
        this.query = this.query.or(`title.ilike.%${keyword}%,content.ilike.%${keyword}%,original_prompt.ilike.%${keyword}%`);
      });
    }
    return this;
  }

  // Apply research type filter - Fixed for jsonb string handling
  applyResearchType(researchType?: string) {
    if (researchType) {
      console.log(`🏷️ Adding research_type filter for jsonb string: ${researchType}`);
      
      try {
        // For jsonb column containing string values, we need to check if the jsonb content equals our string
        // This handles research_type stored as jsonb but containing simple strings like "Technology & Innovation"
        this.query = this.query.eq('research_type', `"${researchType}"`);
      } catch (error) {
        console.warn('Failed to apply research_type filter, skipping:', error);
        // Don't throw - continue with other filters
      }
    }
    return this;
  }

  // Helper function to calculate date threshold based on timeframe
  private calculateDateThreshold(timeframe: string): string {
    const today = new Date();
    let thresholdDate = new Date(today);
    
    switch (timeframe) {
      case 'last_7_days':
      case '7d':
        thresholdDate.setDate(today.getDate() - 7);
        break;
      case 'last_30_days':
      case '30d':
        thresholdDate.setDate(today.getDate() - 30);
        break;
      case 'last_3_months':
      case '3m':
        thresholdDate.setMonth(today.getMonth() - 3);
        break;
      case 'last_6_months':
      case '6m':
        thresholdDate.setMonth(today.getMonth() - 6);
        break;
      case 'last_year':
      case '1y':
        thresholdDate.setFullYear(today.getFullYear() - 1);
        break;
      default:
        return '';
    }
    
    // Return ISO string for created_at timestamp comparison
    return thresholdDate.toISOString();
  }

  // Apply JSONB parameter filters using proper Supabase JavaScript client syntax
  applyParameterFilters(options: UseResearchLibraryOptions) {
    const { timeframe, country, sourceQuality, typeSpecificFilters } = options; // Changed from region to country
    
    console.log('🔧 Applying parameter filters with typeSpecificFilters:', typeSpecificFilters);
    
    try {
      // Add timeframe filter using created_at column (Research Date)
      if (timeframe && timeframe !== 'current') {
        const thresholdDate = this.calculateDateThreshold(timeframe);
        if (thresholdDate) {
          console.log(`📅 Adding created_at filter: created_at >= ${thresholdDate}`);
          this.query = this.query.gte('created_at', thresholdDate);
        }
      }

      // Add country filter (changed from region)
      if (country && country !== 'global') {
        console.log(`🌍 Adding country filter: ${country}`);
        this.query = this.query.filter('research_parameters->>country', 'eq', country);
      }

      // Add source quality filter
      if (sourceQuality && sourceQuality !== 'business') {
        console.log(`⭐ Adding sourceQuality filter: ${sourceQuality}`);
        this.query = this.query.filter('research_parameters->>sourceQuality', 'eq', sourceQuality);
      }

      // Add type-specific filters using proper nested JSONB filtering
      if (typeSpecificFilters && Object.keys(typeSpecificFilters).length > 0) {
        console.log(`🎯 Adding ${Object.keys(typeSpecificFilters).length} type-specific filters:`);
        Object.entries(typeSpecificFilters).forEach(([key, value]) => {
          if (value && typeof value === 'string' && value !== 'none') {
            console.log(`   - ${key} = ${value}`);
            
            // Use proper Supabase JSONB filtering for nested objects
            // Format: research_parameters->type_specific_filters->>key
            this.query = this.query.filter(`research_parameters->type_specific_filters->>${key}`, 'eq', value);
          }
        });
      }

    } catch (error) {
      console.warn('Failed to apply JSONB parameter filters, skipping:', error);
      // Don't throw - continue with base query
    }

    return this;
  }

  // Apply favorites filter with proper join
  applyFavoritesFilter(showFavorites: boolean, userId: string) {
    if (showFavorites) {
      // Reset query for favorites with proper inner join - favorites are always user-specific
      this.query = supabase
        .from('research_library')
        .select(this.baseSelect.replace('!left', '!inner'))
        .eq('user_id', userId)
        .eq('research_favorites.user_id', userId);
    }
    return this;
  }

  // Apply sorting and pagination - Updated with new sorting logic including generated research count
  applySortingAndPagination(pageParam: number, pageSize: number) {
    this.query = this.query
      .order('favorite_count', { ascending: false })        // Most favorites first
      .order('generated_research_count', { ascending: false }) // Then most generative research
      .order('created_at', { ascending: false })             // Then newest first
      .range(pageParam * pageSize, (pageParam * pageSize) + pageSize - 1);
    return this;
  }

  // Execute the query with error handling
  async execute() {
    try {
      const result = await this.query;
      
      if (result.error) {
        throw result.error;
      }

      return result;
    } catch (error) {
      console.error('Query execution failed:', error);
      throw error;
    }
  }

  // Get the raw query for debugging
  getQuery() {
    return this.query;
  }
}

// Utility function to validate query options
export function validateQueryOptions(options: UseResearchLibraryOptions): boolean {
  try {
    // Basic validation
    if (options.pageSize && (options.pageSize < 1 || options.pageSize > 100)) {
      return false;
    }

    // Validate filter values
    if (options.typeSpecificFilters) {
      for (const [key, value] of Object.entries(options.typeSpecificFilters)) {
        if (typeof key !== 'string' || (value !== null && typeof value !== 'string')) {
          return false;
        }
      }
    }

    return true;
  } catch (error) {
    console.warn('Query validation failed:', error);
    return false;
  }
}
